import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { diagnosisSchema } from '@/utils/validation';
import { Diagnosis } from '@/types/diagnosis.types';
import { z } from 'zod';
import { ICD10Autocomplete } from '@/components/common/ICD10Autocomplete';
import { Loader2, Heart, AlertCircle, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type DiagnosisFormData = z.infer<typeof diagnosisSchema>;

interface DiagnosisFormProps {
  patientId: number;
  diagnosis?: Diagnosis | null;
  onSubmit: (data: DiagnosisFormData) => Promise<void>;
  onCancel?: () => void;
}

export const DiagnosisForm: React.FC<DiagnosisFormProps> = ({
  patientId,
  diagnosis,
  onSubmit,
  onCancel,
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<DiagnosisFormData>({
    resolver: zodResolver(diagnosisSchema),
    defaultValues: {
      patient_id: patientId,
      icd10_code: diagnosis?.icd10_code || '',
      severity_level: diagnosis?.severity_level || diagnosis?.severity_id || 3,
      is_primary: diagnosis?.is_primary ?? false,
      diagnosis_date: diagnosis?.diagnosis_date || new Date().toISOString().split('T')[0],
      notes: diagnosis?.notes || undefined,
    },
  });

  const handleSubmit = async (data: DiagnosisFormData) => {
    try {
      setIsLoading(true);
      await onSubmit(data);
      
      toast({
        title: 'Succes',
        description: diagnosis 
          ? 'Diagnosticul a fost actualizat cu succes.' 
          : 'Diagnosticul a fost adăugat cu succes.',
      });
      
      if (!diagnosis) {
        form.reset();
      }
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'A apărut o eroare la salvarea diagnosticului.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityColor = (level: number) => {
    switch (level) {
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-yellow-100 text-yellow-800';
      case 3: return 'bg-orange-100 text-orange-800';
      case 4: return 'bg-red-100 text-red-800';
      case 5: return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityLabel = (level: number) => {
    switch (level) {
      case 1: return 'Foarte ușoară';
      case 2: return 'Ușoară';
      case 3: return 'Moderată';
      case 4: return 'Severă';
      case 5: return 'Critică';
      default: return 'Necunoscută';
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            {/* ICD-10 Code Selection */}
            <FormField
              control={form.control}
              name="icd10_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cod ICD-10 *</FormLabel>
                  <FormControl>
                    <ICD10Autocomplete
                      value={field.value ?? ''}
                      onChange={(code) => field.onChange(code ?? '')}
                      placeholder="Căutați sau selectați un cod ICD-10..."
                    />
                  </FormControl>
                  <FormDescription>
                    Selectați codul ICD-10 corespunzător diagnosticului MCC
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Severity Level */}
            <FormField
              control={form.control}
              name="severity_level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nivel severitate *</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    defaultValue={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selectați severitatea" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((level) => (
                        <SelectItem key={level} value={level.toString()}>
                          <div className="flex items-center gap-2">
                            <Badge className={getSeverityColor(level)}>
                              {level}
                            </Badge>
                            <span>{getSeverityLabel(level)}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Evaluați severitatea afecțiunii pe o scară de la 1 la 5
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Diagnosis Date */}
            <FormField
              control={form.control}
              name="diagnosis_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data diagnosticului *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Primary Diagnosis */}
            <FormField
              control={form.control}
              name="is_primary"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-muted/50">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Diagnostic principal
                    </FormLabel>
                    <FormDescription>
                      Bifați dacă acesta este diagnosticul principal al pacientului
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observații</FormLabel>
                  <FormControl>
                    <textarea
                      className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Observații adiționale despre diagnostic..."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Maximum 500 caractere
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* EUROCAT Mapping Info */}
            <div className="rounded-lg border bg-blue-50 p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-900">
                    Mapare EUROCAT
                  </p>
                  <p className="text-sm text-blue-700">
                    Acest diagnostic va fi automat clasificat conform standardelor EUROCAT pentru raportare europeană.
                  </p>
                </div>
              </div>
            </div>

            {/* Form Actions - sticky so the submit button stays reachable when the
                dialog content is taller than the viewport and scrolls internally */}
            <div className="sticky bottom-0 -mx-6 -mb-5 flex justify-end gap-3 border-t bg-background px-6 py-4">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  <X className="mr-2 h-4 w-4" />
                  Anulează
                </Button>
              )}
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Heart className="mr-2 h-4 w-4" />
                    {diagnosis ? 'Actualizează diagnostic' : 'Adaugă diagnostic'}
                  </>
                )}
              </Button>
            </div>
      </form>
    </Form>
  );
};
